// components/Footer.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  TextField,
  Button,
  Chip,
  Fade,
} from "@mui/material";
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  Phone,
  Email,
  LocationOn,
  Send,
  ArrowForward,
  School,
  Code,
  Security,
  Storage,
  Psychology,
} from "@mui/icons-material";
import Logo from "../assets/Footer/logo.png";
import "./../styles/Footer.css";

const Footer = () => {
  const navigate = useNavigate();

  const quickLinks = [
    { text: "Home", path: "/" },
    { text: "Courses", path: "/courses" },
    { text: "About Us", path: "/about" },
    { text: "Contact", path: "/contact" },
    { text: "Blog", path: "/blog" },
  ];

  const supportLinks = [
    { text: "Help Center", path: "/help" },
    { text: "Privacy Policy", path: "/privacy" },
    { text: "Terms of Service", path: "/terms" },
    { text: "Cookie Policy", path: "/cookies" },
    { text: "Refund Policy", path: "/refund" },
  ];

  const courseCategories = [
    { icon: <Code />, label: "Software Development" },
    { icon: <Psychology />, label: "Artificial Intelligence" },
    { icon: <Security />, label: "Cyber Security" },
    { icon: <Storage />, label: "Cloud Computing" },
    { icon: <School />, label: "Data Science" },
  ];

  const socialLinks = [
    { icon: <Facebook />, url: "https://facebook.com", color: "#1877f2" },
    { icon: <Twitter />, url: "https://twitter.com", color: "#1da1f2" },
    { icon: <Instagram />, url: "https://instagram.com", color: "#e4405f" },
    { icon: <LinkedIn />, url: "https://linkedin.com", color: "#0a66c2" },
    { icon: <YouTube />, url: "https://youtube.com", color: "#ff0000" },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      className="footer-section"
      sx={{
        backgroundColor: "#0a1628",
        color: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(233, 69, 96, 0.03) 0%, transparent 70%)",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(52, 152, 219, 0.03) 0%, transparent 70%)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ py: { xs: 6, md: 8 } }}>
          {/* Column 1: Brand Info */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={600}>
              <Box>
                {/* Logo */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <img
                    src={Logo}
                    alt="Wavepalm Techub"
                    style={{
                      height: 50,
                      width: "auto",
                      filter: "brightness(0) invert(1)",
                    }}
                  />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.6)",
                    lineHeight: 1.8,
                    mb: 3,
                    maxWidth: 400,
                  }}
                >
                  Empowering learners worldwide with cutting-edge tech
                  education. Join thousands of students accelerating their
                  careers with our expert-led courses.
                </Typography>

                {/* Trust Badge */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 3,
                  }}
                >
                  <Chip
                    label="⭐ 4.8/5 Rating"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(46, 204, 113, 0.15)",
                      color: "#2ecc71",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label="🎓 10K+ Students"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(52, 152, 219, 0.15)",
                      color: "#3498db",
                      fontWeight: 600,
                    }}
                  />
                </Box>

                {/* Social Links */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "rgba(255, 255, 255, 0.4)",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        "&:hover": {
                          color: social.color,
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          transform: "translateY(-3px)",
                          boxShadow: `0 4px 15px ${social.color}30`,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid item xs={6} md={2}>
            <Fade in timeout={800}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "#ffffff",
                    mb: 2,
                  }}
                >
                  Quick Links
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {quickLinks.map((link, index) => (
                    <Link
                      key={index}
                      onClick={() => handleNavigation(link.path)}
                      sx={{
                        color: "rgba(255, 255, 255, 0.6)",
                        textDecoration: "none",
                        cursor: "pointer",
                        fontSize: "0.95rem",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        "&:hover": {
                          color: "#e94560",
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <ArrowForward sx={{ fontSize: 14, opacity: 0.5 }} />
                      {link.text}
                    </Link>
                  ))}
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Column 3: Support */}
          <Grid item xs={6} md={2}>
            <Fade in timeout={1000}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "#ffffff",
                    mb: 2,
                  }}
                >
                  Support
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {supportLinks.map((link, index) => (
                    <Link
                      key={index}
                      onClick={() => handleNavigation(link.path)}
                      sx={{
                        color: "rgba(255, 255, 255, 0.6)",
                        textDecoration: "none",
                        cursor: "pointer",
                        fontSize: "0.95rem",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        "&:hover": {
                          color: "#e94560",
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <ArrowForward sx={{ fontSize: 14, opacity: 0.5 }} />
                      {link.text}
                    </Link>
                  ))}
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Column 4: Course Categories & Contact */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1200}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "#ffffff",
                    mb: 2,
                  }}
                >
                  Course Categories
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 3,
                  }}
                >
                  {courseCategories.map((category, index) => (
                    <Chip
                      key={index}
                      icon={category.icon}
                      label={category.label}
                      size="small"
                      onClick={() => handleNavigation("/courses")}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        color: "rgba(255, 255, 255, 0.7)",
                        border: "1px solid rgba(255, 255, 255, 0.05)",
                        "&:hover": {
                          backgroundColor: "rgba(233, 69, 96, 0.1)",
                          color: "#e94560",
                          borderColor: "rgba(233, 69, 96, 0.3)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                        "& .MuiChip-icon": {
                          color: "inherit",
                        },
                      }}
                    />
                  ))}
                </Box>

                {/* Contact Info */}
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      color: "#ffffff",
                      mb: 2,
                    }}
                  >
                    Get in Touch
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        color: "rgba(255, 255, 255, 0.6)",
                      }}
                    >
                      <Email sx={{ fontSize: 20, color: "#e94560" }} />
                      <Typography variant="body2">
                        info@wavepalmtechub.com
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        color: "rgba(255, 255, 255, 0.6)",
                      }}
                    >
                      <Phone sx={{ fontSize: 20, color: "#3498db" }} />
                      <Typography variant="body2">+1 (555) 123-4567</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        color: "rgba(255, 255, 255, 0.6)",
                      }}
                    >
                      <LocationOn
                        sx={{ fontSize: 20, color: "#2ecc71", mt: 0.5 }}
                      />
                      <Typography variant="body2">
                        123 Tech Hub Street,
                        <br />
                        Silicon Valley, CA 94025
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>

        <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }} />

        {/* Bottom Bar */}
        <Box
          sx={{
            py: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.4)",
              fontSize: "0.9rem",
            }}
          >
            © {currentYear} Wavepalm Techub. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Link
              onClick={() => handleNavigation("/privacy")}
              sx={{
                color: "rgba(255, 255, 255, 0.3)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": {
                  color: "#e94560",
                },
                transition: "color 0.3s ease",
              }}
            >
              Privacy Policy
            </Link>
            <Link
              onClick={() => handleNavigation("/terms")}
              sx={{
                color: "rgba(255, 255, 255, 0.3)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": {
                  color: "#e94560",
                },
                transition: "color 0.3s ease",
              }}
            >
              Terms of Service
            </Link>
            <Link
              onClick={() => handleNavigation("/cookies")}
              sx={{
                color: "rgba(255, 255, 255, 0.3)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": {
                  color: "#e94560",
                },
                transition: "color 0.3s ease",
              }}
            >
              Cookie Policy
            </Link>
          </Box>

          {/* <Typography
            variant="caption"
            sx={{
              color: "rgba(255, 255, 255, 0.2)",
              fontSize: "0.75rem",
            }}
          >
            Made with ❤️ in Pakistan
          </Typography> */}
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
